import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ApiError, api } from '../../lib/api'
import { useCategoryNames, useOperatorTimezone } from '../../lib/operator'
import type { RequestDetail as Detail } from '../../lib/optypes'
import { shortDateTime } from '../../lib/optypes'

import { OperatorLayout } from './OperatorLayout'
import { WorkspacePanel } from './WorkspacePanel'

/**
 * Everything Andrew needs to walk into the house knowing the job (01 §4), plus outcome marking
 * (01 §3.7).
 *
 * This is the one screen in the product that shows a real price. The `final_price` field is
 * operator-only by construction — it lives on `outcome`, is never in a homeowner serializer,
 * and is deliberately kept out of the audit payload. The label says so, because the person
 * typing it needs to know it is private.
 */
export function RequestDetail() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const tz = useOperatorTimezone()
  const names = useCategoryNames()

  const { data, isPending, isError } = useQuery({
    queryKey: ['op-request', id],
    queryFn: () => api.get<Detail>(`/op/requests/${id}`),
  })

  if (isPending)
    return (
      <OperatorLayout>
        <p className="text-muted">Loading…</p>
      </OperatorLayout>
    )
  if (isError || !data) {
    return (
      <OperatorLayout>
        <p className="text-muted">Could not load that request.</p>
        <Link to="/op" className="mt-4 inline-block font-semibold text-blue-deep">
          Back to the inbox
        </Link>
      </OperatorLayout>
    )
  }

  const address = [
    data.address_line1,
    data.address_line2,
    data.city,
    `${data.state ?? ''} ${data.zip ?? ''}`.trim(),
  ]
    .filter(Boolean)
    .join(', ')
  const mapQuery = encodeURIComponent(address)

  return (
    <OperatorLayout>
      <Link
        to="/op"
        className="text-sm font-semibold text-blue-deep underline-offset-4 hover:underline"
      >
        ← Inbox
      </Link>

      <h1 className="mt-4 text-2xl">{data.homeowner_name ?? 'No name given'}</h1>
      <p className="mt-1 text-sm text-muted">
        {names(data.categories)} · asked {shortDateTime(data.created_at, tz)}
      </p>

      <section className="mt-6 grid gap-2.5 rounded-card border border-line bg-white p-5">
        <Row label="Email">
          <a href={`mailto:${data.homeowner_email}`} className="text-blue-deep">
            {data.homeowner_email}
          </a>
        </Row>
        <Row label="Phone">
          {data.homeowner_phone ? (
            <a href={`tel:${data.homeowner_phone}`} className="text-blue-deep">
              {data.homeowner_phone}
            </a>
          ) : (
            '—'
          )}
        </Row>
        <Row label="Prefers">{data.contact_pref}</Row>
        <Row label="Address">
          {address ? (
            <a href={`https://maps.google.com/?q=${mapQuery}`} className="text-blue-deep">
              {address}
            </a>
          ) : (
            '—'
          )}
        </Row>
        {data.in_service_area === false && (
          <p className="m-0 mt-1 rounded-btn bg-sand px-3 py-2 text-sm text-ink">
            Outside Monmouth County — check the travel.
          </p>
        )}
      </section>

      <section className="mt-4 rounded-card border border-line bg-white p-5">
        <h2 className="text-lg">Visit</h2>
        {data.booking ? (
          <p className="m-0 mt-2">
            <strong className="whitespace-nowrap text-ink" data-testid="visit-time">
              {shortDateTime(data.booking.slot_start, tz)}
            </strong>
            <span className="ml-2 text-sm text-muted">{data.booking.status}</span>
            {data.booking.rescheduled_from && (
              <span className="ml-2 text-sm text-muted">
                (moved from {shortDateTime(data.booking.rescheduled_from, tz)})
              </span>
            )}
          </p>
        ) : (
          <p className="m-0 mt-2 text-muted">Nothing booked yet.</p>
        )}
      </section>

      {data.description && (
        <section className="mt-4 rounded-card border border-line bg-white p-5">
          <h2 className="text-lg">In their words</h2>
          <p className="m-0 mt-2 whitespace-pre-wrap">{data.description}</p>
        </section>
      )}

      {data.photos.length > 0 && (
        <section className="mt-4 rounded-card border border-line bg-white p-5">
          <h2 className="text-lg">Photos</h2>
          <ul className="m-0 mt-3 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-3">
            {data.photos.map((photo) => (
              <li key={photo.id}>
                <a href={photo.url} target="_blank" rel="noreferrer">
                  <img
                    src={photo.url}
                    alt=""
                    loading="lazy"
                    className="aspect-square w-full rounded-img object-cover"
                  />
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.workspace && <WorkspacePanel workspace={data.workspace} />}

      <OutcomeForm
        detail={data}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['op-request', id] })}
      />
    </OperatorLayout>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // Label above the value until there is room beside it. A fixed 7rem label column plus an
    // email address is 332px of content in a 320px viewport, and grid children do not shrink
    // below their content by default — so the page scrolled sideways rather than wrapping.
    // `min-w-0` is what lets the value column shrink; `break-words` is what lets the address
    // inside it wrap once it can.
    <p className="m-0 grid gap-x-3 text-sm min-[380px]:grid-cols-[6.5rem_minmax(0,1fr)]">
      <span className="text-muted">{label}</span>
      <span className="min-w-0 break-words text-ink">{children}</span>
    </p>
  )
}

function OutcomeForm({ detail, onSaved }: { detail: Detail; onSaved: () => void }) {
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/op/requests/${detail.id}/outcome`, body),
    onSuccess: () => {
      setError(null)
      onSaved()
    },
    onError: (err) =>
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Could not save.',
      ),
  })

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const price = String(form.get('final_price') ?? '').trim()
    save.mutate({
      status: form.get('status'),
      final_price: price ? price : null,
      line_items: form.get('line_items') ? Number(form.get('line_items')) : null,
      notes: form.get('notes') || null,
    })
  }

  return (
    <section className="mt-4 rounded-card border border-line bg-white p-5">
      <h2 className="text-lg">After the visit</h2>
      <p className="mt-1.5 text-sm text-muted">
        Marking this drives the KPIs. Anything you type here is private to the operator app — the
        homeowner never sees it.
      </p>

      <form onSubmit={onSubmit} className="mt-4 grid gap-4">
        <div className="grid gap-1.5">
          <label htmlFor="status" className="text-sm font-semibold text-ink">
            Outcome
          </label>
          <select
            id="status"
            name="status"
            defaultValue={detail.outcome_status ?? 'quoted'}
            className="w-full rounded-btn border border-line-2 bg-white px-3.5 py-2.5"
          >
            <option value="quoted">Quoted</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <label htmlFor="final_price" className="text-sm font-semibold text-ink">
              Price you gave <span className="font-normal text-muted">(private)</span>
            </label>
            <input
              id="final_price"
              name="final_price"
              inputMode="decimal"
              defaultValue={detail.outcome_final_price ?? ''}
              placeholder="12400.00"
              className="w-full rounded-btn border border-line-2 bg-white px-3.5 py-2.5"
            />
          </div>
          <div className="grid gap-1.5">
            <label htmlFor="line_items" className="text-sm font-semibold text-ink">
              Line items
            </label>
            <input
              id="line_items"
              name="line_items"
              inputMode="numeric"
              className="w-full rounded-btn border border-line-2 bg-white px-3.5 py-2.5"
            />
          </div>
        </div>

        <div className="grid gap-1.5">
          <label htmlFor="notes" className="text-sm font-semibold text-ink">
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            rows={3}
            defaultValue={detail.outcome_notes ?? ''}
            className="w-full rounded-btn border border-line-2 bg-white px-3.5 py-2.5"
          />
        </div>

        {error && (
          <p role="alert" className="m-0 rounded-btn bg-blue-tint px-3.5 py-2.5 text-sm text-ink">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={save.isPending}
          className="rounded-btn bg-blue px-6 py-3 font-bold text-white disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : detail.outcome_status ? 'Update outcome' : 'Record outcome'}
        </button>
      </form>
    </section>
  )
}
